import { RouteType } from "types/RouteType";
import { ServiceStoreType } from "types/ServiceStoreTypes";

/**
 * Defines the route configuration for the Client List view.
 * This route configuration includes details such as the path, layout,
 * associated React components, data grid settings, and store interactions.
 */
const jobListRoute: RouteType[] = [
  {
    /**
     * The user-friendly name of the route, displayed in navigation elements.
     */
    name: "Client List",
    hidden: true,
    /**
     * The path associated with this route.  Navigating to '/client-list' will render this view.
     */
    path: "/client-list",
    /**
     * The layout to use for this route.  Specifies the base layout component under '/app'.
     */
    layout: "/app",
    /**
     * Configuration for the viewport card, which defines the header and body content.
     */
    viewportCard: {
      /**
       * Configuration for the header section of the viewport card.
       */
      header: {
        /**
         * The title displayed in the header.
         */
        title: "Client List",
        /**
         * A React component to render in the header.  Dynamically imported.
         */
        component: () => import("./ClientList.Header"),
        /**
         * Enables a refresh button in the header, likely to refresh the data in the grid.
         */
        enableRefreshButton: true,
        /**
         * Enables a back button in the header, allowing navigation to the previous view.
         */
        enableBackButton: true,
      },
      /**
       * Configuration for the body section of the viewport card, primarily the data grid.
       */
      body: {
        /**
         * Configuration for the data grid.
         */
        gridContainer: {
          /**
           * A unique namespace for this data grid, used for state management or identification.
           */
          namespace: "ClientListDataGrid",
          /**
           * The name of the MobX store that provides the data for this grid.
           */
          storeName: "jobStore",
          /**
           * Defines the column headers for the data grid.
           * Each object represents a column with properties like width, title, key (data field), and data type.
           */
          columnHeaders: [
            {
              /**
               * The width of the column in pixels.
               */
              width: 150,
              /**
               * The title displayed in the column header.
               */
              title: "Client Id",
              /**
               * The key that maps to the data field in the store.
               */
              key: "jobNumber",
              /**
               * The data type of the column, used for formatting or validation.
               */
              dataType: "number",
              /**
               * Disables editing for this column.
               */
              disableEdit: true,
            },
            {
              width: 200,
              title: "Name",
              key: "name",
            },
            {
              width: 400,
              title: "Address",
              key: "address",
            },
            {
              width: 200,
              title: "City",
              key: "city",
            },
            {
              width: 200,
              title: "State",
              key: "stateLocation",
            },
            {
              width: 200,
              title: "Zip",
              key: "zip",
            },
            {
              width: 200,
              title: "Archived",
              key: "archived",
              dataType: "boolean",
            },
          ],
          /**
           * Enables inline editing of grid cells.
           */
          enableInlineEdit: true,
          /**
           * Enables hover effects on grid rows.
           */
          enableHover: true,
          /**
           * A callback function triggered when edits are submitted in the grid.
           * @param id - The ID of the row being edited.
           * @param data - The edited data.
           * @param rootStore - The root MobX store, providing access to all stores.
           */
          onSubmitEdits: (id, data, rootStore) => {
            // Accesses the jobStore from the rootStore.
            const jobStore = rootStore.jobStore as ServiceStoreType;
            // Patches the data for the specified ID in the jobStore.
            jobStore.patch(Number(id), data as object);
          },
          /**
           * A callback function triggered when a row is removed from the grid.
           * @param rowData - The data of the row being removed.
           * @param rootStore - The root MobX store.
           */
          onRemove: (rowData, rootStore) => {
            // Accesses the jobStore from the rootStore.
            const jobStore = rootStore.jobStore as ServiceStoreType;
            // Removes the row with the specified ID from the jobStore.
            jobStore.remove((rowData as { id: number }).id);
          },
        },
      },
    },
    group: "CRM",
    /**
     * Indicates whether the route requires specific permissions.  Set to false for this route.
     */
    requiresPermission: false,
    /**
     * Indicates whether the route requires authentication.  Set to false for this route.
     */
    requiresAuth: false,
    /**
     * An array of channels associated with this route, likely used for real-time updates or notifications.
     */
    channels: ["job"], // Keep channels
    /**
     * Defines dialogs associated with this route, such as a "Create Client" dialog.
     */
    dialogs: [
      {
        /**
         * The name of the dialog.
         */
        name: "Create Client",
        /**
         * The title displayed in the dialog.
         */
        title: "Create Client",
        /**
         * A React component to render in the dialog.  Dynamically imported.
         */
        component: () =>
          import("./ClientListDialogs/ClientList.CreateClient.Dialog"),
      },
    ],
    /**
     * Enables automatic finding of data when the route is entered.
     */
    enableAutoFind: false,
    /**
     * A callback function triggered when the route is entered.
     * Used to fetch data or perform other initialization tasks.
     * @param {object} params - An object containing parameters, including the rootStore.
     * @param {object} params.rootStore - The root MobX store.
     */
    onEnter: async ({ rootStore }) => {
      // Accesses the jobStore from the rootStore.
      const jobStore = rootStore.jobStore as ServiceStoreType;
      const jobStatsPanelCountStore =
        rootStore.jobStatsPanelCountStore as ServiceStoreType;

      // Fetches data for the jobStore.
      jobStore.find();
      jobStatsPanelCountStore.find();
    },
  },
];

export default jobListRoute;
